'use client'
import Link from 'next/link'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { ArrowRight, Target, Clock, Play, Sparkles } from 'lucide-react'
import { StarIcon } from './medallions/star-icon'

export interface NextRoundCTAProps {
  title?: string
  difficulty: string        // e.g. 'T3 · Medium'
  durationMin: number
  expectedStars: number
  pillarFocusCode: string   // e.g. 'PF3'
  playHref?: string         // default '/pt-play' (independent, chrome-free)
  /** When true, masks the tier/difficulty and focus pillar with a teaser. */
  mystery?: boolean
}

export function NextRoundCTA({
  title = 'Your next training round',
  difficulty,
  durationMin,
  expectedStars,
  pillarFocusCode,
  playHref = '/pt-play',
  mystery,
}: NextRoundCTAProps) {
  return (
    <div className="relative overflow-hidden rounded-2xl border border-primary/30 bg-primary/5 p-5">
      <div className="absolute -top-12 -right-12 w-40 h-40 rounded-full opacity-30 blur-3xl bg-primary" />
      <div className="relative flex flex-col sm:flex-row sm:items-center gap-5">
        <div className="flex h-16 w-16 flex-shrink-0 items-center justify-center rounded-2xl bg-primary text-primary-foreground shadow-lg">
          <Play className="h-7 w-7 fill-current" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-primary font-semibold">Next mission</div>
          <div className="text-base font-semibold text-foreground">{title}</div>
          {mystery ? (
            <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
              <span className="inline-flex items-center gap-1 font-semibold text-primary">
                <Sparkles className="h-3 w-3" /> Unknown threat · Mystery round
              </span>
              <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> ~{durationMin} min</span>
              <span>Difficulty revealed on start.</span>
            </div>
          ) : (
            <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
              <span className="inline-flex items-center gap-1"><Target className="h-3 w-3" /> {difficulty}</span>
              <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> ~{durationMin} min</span>
              <span className="inline-flex items-center gap-1">Up to <StarIcon size={11} /> <span className="font-semibold text-foreground">{expectedStars}</span> stars</span>
              <span className="inline-flex items-center gap-1">Focus: <Badge variant="outline" className="text-[10px]">{pillarFocusCode}</Badge></span>
            </div>
          )}
        </div>
        <Button asChild size="lg" className="shadow-lg">
          <Link href={playHref}>Play <ArrowRight className="h-4 w-4 ml-1" /></Link>
        </Button>
      </div>
    </div>
  )
}
